import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  OnChanges,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { ECharts } from 'echarts';
import { chartOptions } from '../../echart/echart-constant/pie-chart-options';
import { EChartsOption } from 'echarts';
import { CurrencyPipe } from '@angular/common';
import { colorList } from '../../../utils/utils.function';

@Component({
    selector: 'app-account-balance-chart',
    templateUrl: './account-balance-chart.component.html',
    styleUrls: ['./account-balance-chart.component.scss'],
    providers: [CurrencyPipe],
    standalone: false
})
export class AccountBalanceChartComponent implements OnInit, OnChanges {
  chartInstance!: ECharts | null;
  chartOptions: EChartsOption = { ...chartOptions };
  @Input() chartValueLabel: string = 'Job Value';
  scopeChartView: boolean = false;
  @Input() scopeList: any[] = [];
  private _statistics: any;
  @Input() set statistics(value: any) {
    // Stage 6 Debug: Log when statistics changes
    console.log('📈 Stage 6 - Chart Component statistics Changed:', {
      total_tax: value?.total?.tax,
      full_value: value
    });
    this._statistics = value;
  }
  get statistics(): any {
    return this._statistics;
  }
  @Input() model: any;
  @Input() isFinancialOverviewInvoiced: boolean = false;
  @Input() isFinancialOverviewRemaining: boolean = false;
  @Input() isFinancialOverviewInvoicePercentage: boolean = false;
  chartValueLabelCopy: string = '';

  private _taxValue: number = 0;
  @Input() set taxValue(value: number) {
    // Stage 6 Debug: Log when taxValue changes
    console.log('💳 Stage 6 - Chart Component taxValue Changed:', {
      old_value: this._taxValue,
      new_value: value,
      stack: new Error().stack
    });
    this._taxValue = value;
  }
  get taxValue(): number {
    return this._taxValue;
  }
  private _chartAmountValue: any = '$0.00';
  chartAmountValueCopy: string = '';

  // New properties for enhanced UI
  completionPercentage: number = 70;
  centerDisplayAmount: string = '$96,022.00';
  budgetAmount: string = '$0.00';
  spentAmount: string = '$0.00';
  remainingAmount: string = '$0.00';
  invoicedAmount: string = '$0.00';
  invoicedPercentage: number = 0;
  remainingPercentage: number = 100;
  jobTotalAmount: number = 0;
  retainageAmount: number = 0;
  retainageDisplay: string = '$0.00';
  retainagePercent: number = 0;

  @Input() set chartAmountValue(value: any) {
    this._chartAmountValue = value;
    this.chartAmountValueCopy = value;
    // You can add additional logic here if needed when the value changes
  }

  get chartAmountValue(): any {
    return this._chartAmountValue;
  }
  private clickListener: () => void;

  constructor(
    private currencyPipe: CurrencyPipe,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private elRef: ElementRef
  ) {
    this.clickListener = this.renderer.listen(
      'document',
      'click',
      (event: MouseEvent) => this.onDocumentClick(event)
    );
  }

  ngOnInit(): void {
    this.calculateKPIMetrics();
  }

  ngAfterViewInit(): void {
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.
    this.chartValueLabelCopy = this.chartValueLabel;
  }

  ngOnChanges(changes: SimpleChanges): void {
    //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    //Add '${implements OnChanges}' to the class.
    if (changes) {
      if (changes['scopeList']?.currentValue) {
        this.scopeList = changes['scopeList']?.currentValue;
      }
      if (changes['statistics']?.currentValue) {
        this.statistics = changes['statistics']?.currentValue;
      }
      if (changes['model']?.currentValue) {
        this.model = changes['model']?.currentValue;
      }
      this.calculateKPIMetrics();
      this.setChartOptions();
    }
  }

  setChartOptions() {
    // Use a small delay to ensure the chart clears before applying new options
    setTimeout(() => {
      if (!this.scopeChartView) {
        this.setProfitAndCostData();
      } else if (this.scopeChartView) {
        this.setScopeListData();
      }

      // Update chart positioning and gradient
      this.updateChartStyle();

      this.chartInstance?.setOption(this.chartOptions, { notMerge: true });
      this.listenChartEvent();
    }, 100); // 100ms delay to ensure the clear takes effect
  }

  getChartInstance(event: ECharts) {
    this.chartInstance = event;

    if (this.chartInstance) {
      // Clear the chart before setting new options
      this.chartInstance.clear();
      this.setChartOptions();
    } else {
      console.error('Chart instance not initialized');
    }
  }

  private setProfitAndCostData() {
    const profitValue = Math.abs(Number(this.statistics?.profit?.total)); // Use absolute value
    const costValue = Math.abs(Number(this.statistics?.cost?.totalCost)); // Use absolute value
    const taxValue = Math.abs(Number(this.taxValue)); // Use absolute value
    const data = [
      {
        value: profitValue,
        name: 'Total Profit',
        id: 'profit',
        color: '#008000',
      },
      { value: costValue, name: 'Total Cost', id: 'cost', color: '#FF0000' },
      { value: taxValue, name: 'Tax', id: 'tax', color: '#6e2c00' },
    ];

    this.setChartSeriesData(data, 'Proposal');
    this.setTooltipFormatterForProfitAndCost();
  }

  private setScopeListData() {
    this.scopeList.forEach((x: any, index: number) => {
      x.name = x.scope_name;
      x.value = Math.abs(Number(x.total));
      // Assign a unique color to each scope segment from the colorList
      x.color = colorList[index % colorList.length];
    });

    this.setChartSeriesData(this.scopeList, 'Proposal');
    this.setTooltipFormatterForScopeList();
  }

  private setChartSeriesData(data: any[], seriesName: string) {
    // Ensure series is defined and access the first series
    const series = this.chartOptions.series as any[];
    if (series && series.length > 0) {
      series[0].name = seriesName;
      series[0].data = data; // Access the first series and set its data
    } else {
      console.error('Series array is undefined or empty');
    }
  }

  private setTooltipFormatterForProfitAndCost() {
    this.chartOptions.tooltip = {
      position: (point: any, params, dom, rect, size: any) =>
        this.calculateTooltipPosition(point, size),
      confine: true, // Keep tooltip within chart container
      formatter: (params: any) => {
        const formattedValue = this.currencyPipe.transform(params.value, 'USD');
        return `${params.name}: ${formattedValue}`;
      },
    };
  }

  private setTooltipFormatterForScopeList() {
    this.chartOptions.tooltip = {
      position: (point: any, params, dom, rect, size: any) =>
        this.calculateTooltipPosition(point, size),
      confine: true, // Keep tooltip within chart container
      formatter: (params: any) => {
        let profit = this.currencyPipe.transform(
          params.data.profit.total,
          'USD'
        );
        let cost = Number(params.data.cost.total);
        let name = params.name;
        let costWithTax = this.currencyPipe.transform(cost, 'USD');
        let tax = this.currencyPipe.transform(params.data.total_tax, 'USD');
        let total = this.currencyPipe.transform(params.data.total, 'USD');
        return `Scope: ${name}<br> Profit: ${profit}<br> Cost: ${costWithTax} <br> Tax: ${tax} <br> Total: ${total}`;
      },
    };
  }

  private calculateTooltipPosition(point: any, size: any) {
    const contentSize = (size as { contentSize: [number, number] }).contentSize;
    const viewSize = (size as { viewSize: [number, number] }).viewSize;

    // Calculate initial position
    let x = point[0] + 10;
    let y = point[1] - contentSize[1] - 10;

    // Ensure tooltip stays within horizontal bounds
    if (x + contentSize[0] > viewSize[0]) {
      // If tooltip would go off right edge, position it to the left of cursor
      x = point[0] - contentSize[0] - 10;
    }

    // Ensure x doesn't go negative
    if (x < 0) {
      x = 10; // Small margin from left edge
    }

    // Ensure tooltip stays within vertical bounds
    if (y < 0) {
      // If not enough space above, position below
      y = point[1] + 10;
    }

    // Ensure y doesn't go below viewport
    if (y + contentSize[1] > viewSize[1]) {
      y = viewSize[1] - contentSize[1] - 10;
    }

    return [x, y];
  }

  currentlyHighlighted = {
    seriesIndex: -1,
    dataIndex: -1, // Optionally, if you want to highlight the first data point in the series
  };

  listenChartEvent() {
    if (this.chartInstance) {
      // Remove previous event listeners
      this.chartInstance.off('click');
      this.chartInstance.off('mouseover');
      // Click listener for series item highlighting
      this.chartInstance.on('click', (params: any) => {
        this.listenProposalChartHoverEvent(params);
      });

      // Hover listener for series item highlighting
      this.chartInstance.on('mouseover', (params: any) => {
        this.checkAllReadyActiveSeries(params);
      });

      // Force the chart to resize and repaint
      setTimeout(() => {
        this.chartInstance?.resize();
      }, 200);
    }
  }

  checkAllReadyActiveSeries(params: any) {
    if (this.currentlyHighlighted.seriesIndex !== -1) {
      this.chartInstance?.dispatchAction({
        type: 'downplay',
        seriesIndex: this.currentlyHighlighted.seriesIndex,
        dataIndex: this.currentlyHighlighted.dataIndex,
      });
      this.resetSeries();
    }
  }

  listenProposalChartHoverEvent(params: any) {
    // De-highlight the previously highlighted series item
    if (this.currentlyHighlighted) {
      this.chartInstance?.dispatchAction({
        type: 'downplay',
        seriesIndex: this.currentlyHighlighted.seriesIndex,
        dataIndex: this.currentlyHighlighted.dataIndex,
      });
    }

    // Highlight the clicked series item
    this.chartInstance?.dispatchAction({
      type: 'highlight',
      seriesIndex: params.seriesIndex,
      dataIndex: params.dataIndex,
    });

    // Store the currently highlighted series and data index
    this.currentlyHighlighted = {
      seriesIndex: params.seriesIndex,
      dataIndex: params.dataIndex,
    };

    console.log('Hovered data:', params);
    if (params?.data?.id === 'profit') {
      this.chartValueLabel = 'Total Profit';
      this.chartAmountValueCopy = this.currencyPipe.transform(
        this.statistics.profit.total,
        'USD'
      ) as string;
      console.log(this.chartAmountValue);
    } else if (params?.data?.id === 'cost') {
      this.chartValueLabel = 'Total Cost';
      this.chartAmountValueCopy = this.currencyPipe.transform(
        Number(this.statistics.cost.totalCost),
        'USD'
      ) as string;
    } else if (params?.data?.id === 'tax') {
      this.chartValueLabel = 'Total Tax';
      this.chartAmountValueCopy = this.currencyPipe.transform(
        Number(this.taxValue),
        'USD'
      ) as string;
    }
  }

  onToggleChange(event: any) {
    this.scopeChartView = !this.scopeChartView;
    // Add additional logic based on the toggle state here
    this.getChartInstance(this.chartInstance as ECharts);
  }

  // Event handler for clicks outside the chart
  private onDocumentClick(event: MouseEvent | any): void {
    const clickedElement = event.target as HTMLElement;

    // Check if the clicked element is inside the chart container
    const isInsideChart = this.elRef.nativeElement.contains(clickedElement);

    if (isInsideChart) {
      // Handle clicks inside the chart container (on the canvas or empty space)
      const model = (this.chartInstance as any)?.getModel() as any;
      if (model) {
        // Check if the click event was not on a series (meaning it's on an empty canvas/background)
        const eventParams = (this.chartInstance as any)
          ?.getZr()
          .handler.findHover(event.offsetX, event.offsetY);
        if (!eventParams || !eventParams.target) {
          this.outsideClickContainer();
        }
      }
    } else {
      // this.outsideClickContainer()
    }
  }

  outsideClickContainer() {
    this.chartInstance?.dispatchAction({
      type: 'downplay',
      seriesIndex: this.currentlyHighlighted.seriesIndex,
      dataIndex: this.currentlyHighlighted.dataIndex,
    });
    this.resetSeries();
    this.chartValueLabel = this.chartValueLabelCopy;
    this.chartAmountValueCopy = this.chartAmountValue;
  }

  resetSeries() {
    this.currentlyHighlighted = {
      seriesIndex: -1,
      dataIndex: -1,
    };
  }

  // New methods for enhanced functionality
  calculateKPIMetrics(): void {
    // Use chartAmountValue if available (passed from parent), otherwise fall back to model.jobTotalAmount
    const chartValue = this.parseAmountFromString(this.chartAmountValue);
    this.jobTotalAmount =
      chartValue || this.model?.jobTotalAmount || this.model?.budget || 0;

    // Get invoiced amount - use invoiced_amount if available, otherwise calculate from statistics
    const invoicedValue = this.model?.invoiced_amount || 0;

    // Calculate retainage from the job total
    this.retainagePercent = this.model?.retainage || 0;
    this.retainageAmount = (this.retainagePercent / 100) * this.jobTotalAmount;
    this.retainageDisplay =
      this.currencyPipe.transform(this.retainageAmount, 'USD') || '$0.00';

    // Calculate remaining amount (including retainage)
    const remainingValue = this.jobTotalAmount - invoicedValue;

    // Format amounts
    this.invoicedAmount =
      this.currencyPipe.transform(invoicedValue, 'USD') || '$0.00';
    this.remainingAmount =
      this.currencyPipe.transform(remainingValue, 'USD') || '$0.00';
    this.budgetAmount =
      this.currencyPipe.transform(this.jobTotalAmount, 'USD') || '$0.00';

    // Calculate percentages
    if (this.jobTotalAmount > 0) {
      this.invoicedPercentage = Math.round(
        (invoicedValue / this.jobTotalAmount) * 100
      );
      this.remainingPercentage = Math.round(
        (remainingValue / this.jobTotalAmount) * 100
      );
    } else {
      this.invoicedPercentage = 0;
      this.remainingPercentage = 100;
    }

    // Ensure percentages add up to 100%
    if (this.invoicedPercentage + this.remainingPercentage !== 100) {
      this.remainingPercentage = 100 - this.invoicedPercentage;
    }

    // Set center display amount to job total amount
    this.centerDisplayAmount =
      this.currencyPipe.transform(this.jobTotalAmount, 'USD') || '$0.00';

    // Legacy calculations for backward compatibility
    const spent = this.statistics?.cost?.totalCost || 0;
    this.spentAmount = this.currencyPipe.transform(spent, 'USD') || '$0.00';

    // Calculate completion percentage based on invoiced amount
    this.completionPercentage = this.invoicedPercentage;
  }

  private parseAmountFromString(value: any): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Remove currency symbols and commas, then parse
      const cleanValue = value.replace(/[$,]/g, '');
      return parseFloat(cleanValue) || 0;
    }
    return 0;
  }

  updateChartStyle(): void {
    const seriesList = this.chartOptions.series as any[];
    if (seriesList && seriesList.length > 0) {
      // Update chart positioning and styling to match final vision
      const series = seriesList[0];

      // Set center position
      series.center = ['50%', '50%'];

      // Update radius for proper donut appearance
      series.radius = ['60%', '85%'];

      // Apply gradient color scheme
      series.itemStyle = {
        ...series.itemStyle,
        color: (params: any) => {
          // For profit/cost/tax view, apply gradient to profit
          if (params.data?.id === 'profit') {
            return {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#F58631' },
                { offset: 1, color: '#CC570B' },
              ],
            };
          }
          // For all other segments (including scopes), use the assigned color
          if (params.data?.color) {
            return params.data.color;
          }
          // Fallback color
          return '#F58631';
        },
      };

      // Disable center labels since we're using overlay
      series.label = {
        show: false,
      };

      // Update emphasis styles
      series.emphasis = {
        ...series.emphasis,
        label: {
          show: false, // Keep center content static
        },
      };
    }
  }
}
